import React, { useCallback, useEffect, useState } from "react";
import {
  FaBan,
  FaCheck,
  FaCheckCircle,
  FaPen,
  FaPlus,
  FaShieldAlt,
  FaTimes,
  FaToggleOff,
  FaToggleOn,
  FaTrashAlt,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { canManageLists } from "../../constants/roles";
import "./WhitelistedIocs.css";

const TYPES = [
  { value: "ip",     label: "IP address" },
  { value: "domain", label: "Domain" },
  { value: "host",   label: "Hostname / mail relay" },
  { value: "email",  label: "Email address" },
  { value: "url",    label: "URL" },
];

const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.value, t.label]));

/**
 * Shared CRUD manager for an IoC allow/deny list. Drives both the
 * Whitelist and Blacklist admin screens — they only differ in copy,
 * API endpoint, and accent colour (the `danger` variant).
 *
 * Props:
 *   apiPath       e.g. "/api/admin/whitelist"
 *   title         heading text
 *   subtitle      descriptive blurb
 *   variant       "safe" | "danger"  (controls accent + iconography)
 *   valuePlaceholder / notesPlaceholder
 *   emptyText / forbiddenText
 */
export default function IocListManager({
  apiPath,
  title,
  subtitle,
  variant = "safe",
  valuePlaceholder = "e.g. accenture.com or 10.0.0.5 or notifications@accenture.com",
  notesPlaceholder = "Why is this listed?",
  emptyText = "No entries yet. Add the first one above.",
  forbiddenText = "This section is only available to administrators and contributors.",
}) {
  const { token, user } = useAuth();
  const canManage = canManageLists(user?.role);
  const isDanger = variant === "danger";

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const [form, setForm] = useState({ ioc_type: "domain", value: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  // Inline edit of an existing entry (type / value / notes).
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ ioc_type: "domain", value: "", notes: "" });
  const [editError, setEditError] = useState("");

  const PAGE_SIZE = 8;
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(entries.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const visibleEntries = entries.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(apiPath, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setEntries(json.entries || []);
    } catch (err) {
      setError(err.message || "Failed to load list");
    } finally {
      setLoading(false);
    }
  }, [token, apiPath]);

  useEffect(() => { fetchList(); }, [fetchList]);

  if (!canManage) {
    return (
      <div className={`wl-forbidden ${isDanger ? "wl-variant-danger" : ""}`}>
        <FaShieldAlt />
        <p>{forbiddenText}</p>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.value.trim()) {
      setFormError("Value is required.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(apiPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ioc_type: form.ioc_type,
          value: form.value.trim(),
          notes: form.notes.trim() || null,
        }),
      });
      if (!res.ok) {
        const e2 = await res.json().catch(() => ({}));
        throw new Error(e2.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setEntries((prev) => [json.entry, ...prev]);
      setForm({ ioc_type: form.ioc_type, value: "", notes: "" });
      setPage(0);
    } catch (err) {
      setFormError(err.message || "Failed to add entry");
    } finally {
      setSubmitting(false);
    }
  };

  const onToggle = async (entry) => {
    setBusyId(entry.id);
    try {
      const res = await fetch(`${apiPath}/${entry.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_active: !entry.is_active }),
      });
      if (!res.ok) {
        const e2 = await res.json().catch(() => ({}));
        throw new Error(e2.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setEntries((prev) => prev.map((e) => (e.id === entry.id ? json.entry : e)));
    } catch (err) {
      setError(err.message || "Failed to update entry");
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (entry) => {
    if (!window.confirm(`Delete entry "${entry.value}"? This cannot be undone.`)) return;
    setBusyId(entry.id);
    try {
      const res = await fetch(`${apiPath}/${entry.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const e2 = await res.json().catch(() => ({}));
        throw new Error(e2.error || `HTTP ${res.status}`);
      }
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
    } catch (err) {
      setError(err.message || "Failed to delete entry");
    } finally {
      setBusyId(null);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditForm({
      ioc_type: entry.ioc_type,
      value: entry.value,
      notes: entry.notes || "",
    });
    setEditError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditError("");
  };

  const saveEdit = async (entry) => {
    setEditError("");
    if (!editForm.value.trim()) {
      setEditError("Value is required.");
      return;
    }
    setBusyId(entry.id);
    try {
      const res = await fetch(`${apiPath}/${entry.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ioc_type: editForm.ioc_type,
          value: editForm.value.trim(),
          notes: editForm.notes.trim(),
        }),
      });
      if (!res.ok) {
        const e2 = await res.json().catch(() => ({}));
        throw new Error(e2.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setEntries((prev) => prev.map((e) => (e.id === entry.id ? json.entry : e)));
      setEditingId(null);
    } catch (err) {
      setEditError(err.message || "Failed to save changes");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className={`wl-page ${isDanger ? "wl-variant-danger" : ""}`}>
      <div className="wl-header">
        <h2>{title}</h2>
        <p className="wl-sub">{subtitle}</p>
      </div>

      {/* ── Add form ── */}
      <form className="wl-form" onSubmit={onSubmit}>
        <div className="wl-form-row">
          <label className="wl-form-field wl-field-type">
            <span>Type</span>
            <select
              value={form.ioc_type}
              onChange={(e) => setForm((f) => ({ ...f, ioc_type: e.target.value }))}
            >
              {TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </label>
          <label className="wl-form-field wl-field-value">
            <span>Value</span>
            <input
              type="text"
              placeholder={valuePlaceholder}
              value={form.value}
              onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
            />
          </label>
          <label className="wl-form-field wl-field-notes">
            <span>Notes (optional)</span>
            <input
              type="text"
              placeholder={notesPlaceholder}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </label>
          <div className="wl-form-field wl-field-submit">
            {/* Invisible label keeps this cell structurally identical to the
                input cells, so the button lines up with them exactly. */}
            <span aria-hidden="true">&nbsp;</span>
            <button type="submit" className="wl-form-submit" disabled={submitting}>
              <FaPlus />
              <span>{submitting ? "Adding…" : "Add"}</span>
            </button>
          </div>
        </div>
        {formError && <div className="wl-form-error">{formError}</div>}
      </form>

      {error && <div className="wl-error">{error}</div>}

      {/* ── List ── */}
      {loading ? (
        <div className="wl-loading">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="wl-empty">
          {isDanger ? <FaBan /> : <FaCheckCircle />}
          <p>{emptyText}</p>
        </div>
      ) : (
        <>
          <div className="wl-list">
            {visibleEntries.map((e) =>
              editingId === e.id ? (
                <div key={e.id} className="wl-row wl-row-editing">
                  <select
                    className="wl-edit-input wl-edit-type"
                    value={editForm.ioc_type}
                    onChange={(ev) => setEditForm((f) => ({ ...f, ioc_type: ev.target.value }))}
                    disabled={busyId === e.id}
                  >
                    {TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <div className="wl-edit-fields">
                    <input
                      type="text"
                      className="wl-edit-input"
                      placeholder={valuePlaceholder}
                      value={editForm.value}
                      onChange={(ev) => setEditForm((f) => ({ ...f, value: ev.target.value }))}
                      disabled={busyId === e.id}
                      autoFocus
                    />
                    <input
                      type="text"
                      className="wl-edit-input"
                      placeholder={notesPlaceholder}
                      value={editForm.notes}
                      onChange={(ev) => setEditForm((f) => ({ ...f, notes: ev.target.value }))}
                      disabled={busyId === e.id}
                    />
                    {editError && <div className="wl-form-error wl-edit-error">{editError}</div>}
                  </div>
                  <div className="wl-row-actions">
                    <button
                      type="button"
                      className="wl-save"
                      onClick={() => saveEdit(e)}
                      disabled={busyId === e.id}
                      title="Save changes"
                    >
                      <FaCheck />
                    </button>
                    <button
                      type="button"
                      className="wl-cancel"
                      onClick={cancelEdit}
                      disabled={busyId === e.id}
                      title="Cancel"
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              ) : (
                <div key={e.id} className={`wl-row ${e.is_active ? "is-active" : "is-inactive"}`}>
                  <span className={`wl-type-pill wl-type-${e.ioc_type}`}>
                    {TYPE_LABEL[e.ioc_type] || e.ioc_type}
                  </span>
                  <div className="wl-value-cell">
                    <div className="wl-value">{e.value}</div>
                    {e.notes && <div className="wl-notes">{e.notes}</div>}
                    <div className="wl-meta">
                      Added by {e.created_by || "—"} · {new Date(e.created_at).toLocaleString()}
                      {!e.is_active && <span className="wl-inactive-tag"> · INACTIVE</span>}
                    </div>
                  </div>
                  <div className="wl-row-actions">
                    <button
                      type="button"
                      className="wl-edit"
                      onClick={() => startEdit(e)}
                      disabled={busyId === e.id}
                      title="Edit"
                    >
                      <FaPen />
                    </button>
                    <button
                      type="button"
                      className={`wl-toggle ${e.is_active ? "on" : "off"}`}
                      onClick={() => onToggle(e)}
                      disabled={busyId === e.id}
                      title={e.is_active ? "Deactivate" : "Reactivate"}
                    >
                      {e.is_active ? <FaToggleOn /> : <FaToggleOff />}
                    </button>
                    <button
                      type="button"
                      className="wl-delete"
                      onClick={() => onDelete(e)}
                      disabled={busyId === e.id}
                      title="Delete"
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                </div>
              )
            )}
          </div>

          {totalPages > 1 && (
            <div className="wl-pager">
              <button
                type="button"
                className="wl-pager-btn"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
              >
                Prev
              </button>
              <span className="wl-pager-info">
                Page {safePage + 1} of {totalPages} · {entries.length} total
              </span>
              <button
                type="button"
                className="wl-pager-btn"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={safePage >= totalPages - 1}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
